import {createEffect, createMemo, createRenderEffect, onCleanup} from 'solid-js';
import type {RequestHistoryOptions} from '@appManagers/appMessagesManager';
import {i18n, LangPackKey} from '@lib/langPack';
import Modes from '@config/modes';
import defineSolidElement, {PassedProps} from '@lib/solidjs/defineSolidElement';
import {ButtonMenuItemOptions} from '@components/buttonMenu';
import ButtonMenuToggle from '@components/buttonMenuToggle';
import styles from '@components/chatTypeMenu/styles.module.scss';

if(import.meta.hot) import.meta.hot.accept();


type ChatType = RequestHistoryOptions['chatType'];

type Props = {
  selected?: ChatType;
  hidden?: boolean;
  onChange?: (type: ChatType) => void;
};

const langKeyMap: Record<ChatType, LangPackKey> = {
  'all': 'AllChats',
  'users': 'UsersOnly',
  'groups': 'GroupsOnly',
  'channels': 'ChannelsOnly'
};

const keys: ChatType[] = ['all', 'users', 'groups', 'channels'];

const ChatTypeMenu = defineSolidElement({
  name: 'chat-type-menu',
  component: (props: PassedProps<Props>) => {
    const selected = createMemo(() => props.selected || 'all');

    const options: ButtonMenuItemOptions[] = keys.map(key => ({
      id: key,
      emptyIcon: true,
      text: langKeyMap[key],
      onClick: () => {
        props.selected = key;
        props.onChange?.(key);
      }
    }));

    // a11y: this is a single-select (radio-style) menu, so each item is a
    // menuitemradio and the active one carries aria-checked. Elements are rebuilt
    // every time the menu opens, so apply on open and whenever selected() changes.
    const applyRadioState = () => {
      options.forEach((option) => {
        if(!option.element) return;
        option.element.setAttribute('role', 'menuitemradio');
        option.element.setAttribute('aria-checked', '' + (option.id === selected()));
      });
    };

    createEffect(() => {
      const option = options.find(({id}) => id === selected());
      if(!option) return;

      option.icon = 'check';
      applyRadioState();
      onCleanup(() => {
        option.icon = undefined;
      });
    });

    const span = <span
      class={`primary checkable-button-menu ${styles.ButtonMenu}`}
      classList={{
        [styles.hidden]: !!props.hidden
      }}
      role="button"
      tabindex={Modes.a11y ? (props.hidden ? -1 : 0) : undefined}
    >{i18n(langKeyMap[selected()])}</span> as HTMLSpanElement;

    const buttonMenu = ButtonMenuToggle({
      container: span,
      buttons: options,
      direction: 'bottom-left',
      onOpen: (_, element) => {
        element.style.bottom = 'unset';
        applyRadioState();
      }
    });

    return <>{buttonMenu}</>;
  }
});

export default ChatTypeMenu;
